<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      One of the key appeals of many video games can be found in the characters that populated their
      virtual worlds.
    </p>
    <p class="mb-5">
      If a video game aims to immerse the player in the gameplay, then the characters must be
      relatable and interesting. Increasingly, players are demanding more and more sophisticated
      characters in their video games.
    </p>
    <p class="mb-5">
      For the characters in your video game to be able to grasp a player’s attention and hold it,
      your characters will need to be reasonably complex. This often means they’ll need to have a
      backstory and be motivated to do the things they do.
    </p>
    <p class="mb-5">
      For more information about what goes into designing a video game character, check out this
      excellent Masterclass article. It’s about a 5-minute read - but packed with useful
      information!
    </p>

    <a
      href="https://www.masterclass.com/articles/how-to-design-a-video-game-character#how-to-design-a-video-game-character"
      target="_blank"
    >
      <centered-image-wrapper
        alt="Read how to design a video game character (opens in a new tab)"
        :image="image1"
      />
    </a>

    <h4 class="mb-5">Character Profiles</h4>
    <p class="mb-5">
      One way to help ensure your characters are well-rounded and believable is to start by creating
      a profile for them.
    </p>
    <p class="mb-5">Here’s an example Character Profile for Luke Skywalker:</p>

    <centered-image-wrapper
      :image="image2"
      classes="2/3"
      alt="Example character profile for Luke Skywalker. Name: Luke Skywalker. Age: 19. Gender: Male. Occupation: Farmer. Home: a desolate planet at the edge of the universe. Physical appearance: average height, somewhat athletic. Goal and purpose: to rescue princess, to follow in father's footsteps, to avenge his father. Personality traits: adventurous, brave, humble, selfless, resourceful, intelligent. Character flaws: overconfident, impulsive, hotheaded, impatient, rebellious, whiny. Backstory: Luke is an orphan separated from his parents as a baby and raised by his aunt and uncle. His father was a famous warrior who died mysteriously, but this has been kept a secret from Luke. He works on a farm with his uncle but dreams of adventure and plans to join the imperial academy. He is a great amateur pilot, technically proficient, intelligent, resourceful, and sees the good in people. He is impatient and fantasises about adventure rather than living in the present, which leads to impulsivity and hotheadedness."
    />

    <h4 class="mb-5">Task 1: Create Character Profiles:</h4>

    <p class="mb-5">
      Now, you know your video game genre, you should begin to have an idea of the characters you
      might include in your new video game. For this task, you will create brief character profiles
      for 2 potential characters in your new game.
    </p>
    <p class="mb-5">
      You can choose any 2 characters, but it is sometimes fun to do the main character (usually a
      hero of some sort) and the bad guy or gal.
    </p>
    <p class="mb-5">
      Start by filling in the template below for each character. Remember to keep things brief.
      There’s no need to write an essay here. Keep things short and to the point, and you can use
      key words and phrases to make your points rather than long, complex sentences.
    </p>

    <table class="table default-table" v-for="index in 2" :key="'character_' + index">
      <tr class="header">
        <th colspan="2">Character Profile #{{ index }}</th>
      </tr>
      <tr class="body" v-for="(item, index2) in character_info" :key="item.title">
        <td class="subheader text-left align-top pl-5" width="30%">{{ item.name }}</td>
        <td width="70%" class="smallpadding">
          <text-block
            v-model="data['question_' + index + '_' + index2]"
            v-if="item.type == 'text'"
            :editable="isEditable"
          />
          <textarea-block
            v-model="data['question_' + index + '_' + index2]"
            v-if="item.type == 'textarea'"
            :editable="isEditable"
          />
        </td>
      </tr>
    </table>
    <h4 class="mb-5">Your Character’s Appearance</h4>

    <p class="mb-5">
      Of course, video games are an extremely visual experience and what your characters look like
      is extremely important. While in real-life looks can often be deceiving, in video games, how a
      character looks is often used to tell the player something about their character.
    </p>
    <p class="mb-5">For example,</p>
    <p class="mb-5">
      Which of these images would you expect to be the hero in a video game, and which would you
      expect to be the villain?
    </p>

    <div class="vx-row mt-10 mb-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <img
          :src="image3"
          class="img-responsive mx-auto"
          style="max-height: 400px"
          alt="Character 1: a cartoon woman in a fitted red and orange costume with an eye mask, standing with her hands on her hips."
        />
        <select
          class="default-select mt-5"
          v-model="data['question3']"
          :name="'question_3'"
          aria-label="Who do you expect to be the hero?"
        >
          <option v-for="answer in villain_answers" :key="answer.val" :value="answer.val">
            {{ answer.answer }}
          </option>
        </select>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <img
          :src="image4"
          class="img-responsive mx-auto"
          style="max-height: 400px"
          alt="Character 2: a cartoon figure in a dark hooded robe with a red-eyed skull emblem, holding a glowing green orb above one outstretched hand."
        />
        <select
          class="default-select mt-5"
          v-model="data['question4']"
          :name="'question_4'"
          aria-label="Who do you expect to be the villain?"
        >
          <option v-for="answer in villain_answers" :key="answer.val" :value="answer.val">
            {{ answer.answer }}
          </option>
        </select>
      </div>
    </div>

    <h4 class="mb-5">Task 2: Creating Characters:</h4>
    <p class="mb-5">
      Using your character profiles to help you, you’re going to use the fun “Character Creator”
      tool to help you design two characters. When you’ve designed your characters, you can download
      them as PNG files. Once you’ve downloaded each character’s image, you can then upload the
      images using the upload buttons below.
    </p>

    <p class="mb-5 highlight-text">
      To assist you with this task, you will see two buttons below. The one on the left opens the
      “Character Creator” website, and the one on the right plays a short video that walks you
      through how to use it and what to do.
    </p>

    <div class="vx-row mt-10 mb-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <a href="https://charactercreator.org/" target="_blank">
          <img
            :src="image5"
            alt="Open Character Creator (opens in a new tab)"
            class="img-responsive mx-auto"
            style="max-height: 400px"
          />
        </a>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full mb-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: Using Character Creator"
          @click="showVideoPopup(popupVideo)"
        >
          <img
            :src="image6"
            class="img-responsive mx-auto cursor-pointer"
            style="max-height: 400px"
            alt=""
          />
        </button>
      </div>
    </div>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_' + index]"
      :is-editable="isEditable"
      :index="index"
      filetype="media"
      v-for="index in 2"
      :key="'file_' + index"
      :upload-btn-text="'Upload Character ' + index"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/gamesdeveloper/basic_principles_of_game_design_splash.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/gamesdeveloper/basic_principles_of_game_design.mp4',
        },
      },
      popupVideo:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
        '/content/gamesdeveloper/using_character_creator_1000.mp4',
      image1: require('@/assets/images/experiences/gamesdeveloper/how_to_design_a_video_game_character_button.jpg'),
      image2: require('@/assets/images/experiences/gamesdeveloper/luke_skywalker_character_profile.jpg'),
      image3: require('@/assets/images/experiences/gamesdeveloper/activity_3_1.png'),
      image4: require('@/assets/images/experiences/gamesdeveloper/activity_3_2.png'),
      image5: require('@/assets/images/experiences/gamesdeveloper/character_creator_button.png'),
      image6: require('@/assets/images/experiences/gamesdeveloper/using_character_creater_trigger.jpg'),
      character_info: [
        { name: 'Name:', type: 'text' },
        { name: 'Age:', type: 'text' },
        { name: 'Gender:', type: 'text' },
        { name: 'Role:', type: 'text' },
        { name: 'Home:', type: 'text' },
        { name: 'Physical Appearance:', type: 'textarea' },
        { name: 'Personality Traits:', type: 'textarea' },
        { name: 'Character Flaws:', type: 'textarea' },
        { name: 'Goal & Purpose:', type: 'textarea' },
        { name: 'Backstory:', type: 'textarea' },
      ],
      villain_answers: [
        { val: undefined, answer: 'Select Hero or Villain' },
        { val: 1, answer: 'Hero' },
        { val: 2, answer: 'Villain' },
      ],
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
